/**
 * Client Excel utilisant @devana/ws-tools
 */

import { DevanaWSClient } from "@devana/ws-tools";

// Créer le client Devana pour Excel
export function createExcelClient(
  apiUrl: string,
  options?: {
    documentName?: string;
    bearerToken?: string;
    callbacks?: {
      onConnected?: (sessionId: string) => void;
      onReconnected?: (sessionId: string) => void;
      onDisconnected?: () => void;
      onError?: (error: Error) => void;
      onSessionsChanged?: () => void;
    };
  }
) {
  // Essayer de récupérer le nom du classeur depuis Excel
  let docName = options?.documentName;

  if (!docName && typeof Office !== "undefined" && Office.context?.document) {
    try {
      docName =
        (Office.context.document as any).url
          ?.split("/")
          .pop()
          ?.replace(".xlsx", "") || "Classeur Excel";
    } catch (e) {
      docName = "Classeur Excel";
    }
  }

  const client = new DevanaWSClient({
    apiUrl,
    clientType: "excel",
    documentName: docName,
    bearerToken: options?.bearerToken,
    autoReconnect: true,
    maxReconnectAttempts: 5,
    reconnectDelay: 2000,
    metadata: {
      host: (Office?.context?.host as any) || "Excel",
      platform: (Office?.context?.platform as any) || "unknown",
    },
    onConnected: (sessionId) => {
      console.log("[ExcelClient] ✅ Connected with session:", sessionId);
      console.log("[ExcelClient] Workbook:", docName);

      console.log("[ExcelClient] 📝 Re-registering tools after connection...");
      registerExcelTools(client);

      options?.callbacks?.onConnected?.(sessionId);
    },
    onReconnected: (sessionId) => {
      console.log("[ExcelClient] 🔄 Reconnected with session:", sessionId);

      console.log("[ExcelClient] 📝 Re-registering tools after reconnection...");
      registerExcelTools(client);

      options?.callbacks?.onReconnected?.(sessionId);
    },
    onDisconnected: () => {
      console.log("[ExcelClient] ⚠️ Disconnected");
      options?.callbacks?.onDisconnected?.();
    },
    onError: (error) => {
      console.error("[ExcelClient] ❌ Error:", error);
      options?.callbacks?.onError?.(error);
    },
    onSessionsChanged: () => {
      console.log("[ExcelClient] 🔄 Sessions changed notification");
      options?.callbacks?.onSessionsChanged?.();
    },
  });

  // Enregistrer tous les tools Excel
  registerExcelTools(client);

  return client;
}

/**
 * Enregistre tous les tools Excel avec leurs handlers
 */
function registerExcelTools(client: DevanaWSClient) {
  // === WORKBOOK INFO TOOLS ===

  client.registerTool("get_workbook_info", {
    description: "Get workbook information including sheets and properties",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const workbook = context.workbook;
        const worksheets = workbook.worksheets;
        worksheets.load("items");

        const application = context.workbook.application;
        application.load(["calculationMode"]);

        await context.sync();

        const sheets = worksheets.items.map((sheet, index) => {
          sheet.load(["name", "id", "visibility"]);
          return context.sync().then(() => ({
            name: sheet.name,
            id: sheet.id,
            index: index,
            visibility: sheet.visibility,
          }));
        });

        const sheetsInfo = await Promise.all(sheets);

        return {
          sheetsCount: worksheets.items.length,
          sheets: sheetsInfo,
          calculationMode: application.calculationMode,
        };
      });
    },
  });

  // === WORKSHEET NAVIGATION ===

  client.registerTool("get_active_worksheet", {
    description: "Get the currently active worksheet information",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        worksheet.load(["name", "id"]);

        const usedRange = worksheet.getUsedRange();
        usedRange.load(["rowCount", "columnCount", "address"]);

        await context.sync();

        return {
          name: worksheet.name,
          id: worksheet.id,
          usedRange: {
            rowCount: usedRange.rowCount,
            columnCount: usedRange.columnCount,
            address: usedRange.address,
          },
        };
      });
    },
  });

  client.registerTool("activate_worksheet", {
    description: "Activate a specific worksheet by name",
    schema: {
      type: "object",
      properties: {
        sheetName: {
          type: "string",
          description: "Name of the worksheet to activate",
        },
      },
      required: ["sheetName"],
    },
    handler: async (args) => {
      const { sheetName } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getItem(sheetName);
        worksheet.activate();
        await context.sync();

        return {
          success: true,
          activatedSheet: sheetName,
        };
      });
    },
  });

  // === CELL AND RANGE READING ===

  client.registerTool("get_cell_value", {
    description: "Get value from a specific cell",
    schema: {
      type: "object",
      properties: {
        cellAddress: {
          type: "string",
          description: "Cell address (e.g., 'A1', 'B2')",
        },
        sheetName: {
          type: "string",
          description: "Sheet name (optional, uses active sheet if not provided)",
        },
      },
      required: ["cellAddress"],
    },
    handler: async (args) => {
      const { cellAddress, sheetName } = args;

      return Excel.run(async (context) => {
        const worksheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet();

        const cell = worksheet.getRange(cellAddress);
        cell.load(["values", "formulas", "numberFormat", "text"]);

        await context.sync();

        return {
          address: cellAddress,
          value: cell.values[0][0],
          formula: cell.formulas[0][0],
          numberFormat: cell.numberFormat[0][0],
          text: cell.text[0][0],
        };
      });
    },
  });

  client.registerTool("get_range_values", {
    description: "Get values from a range of cells",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range address (e.g., 'A1:C10', 'Sheet1!A1:B5')",
        },
        includeFormulas: {
          type: "boolean",
          description: "Include formulas in the result",
        },
      },
      required: ["rangeAddress"],
    },
    handler: async (args) => {
      const { rangeAddress, includeFormulas = false } = args;

      return Excel.run(async (context) => {
        let range;

        // Check if rangeAddress includes sheet name
        if (rangeAddress.includes("!")) {
          const [sheetName, rangePart] = rangeAddress.split("!");
          const worksheet = context.workbook.worksheets.getItem(sheetName);
          range = worksheet.getRange(rangePart);
        } else {
          const worksheet = context.workbook.worksheets.getActiveWorksheet();
          range = worksheet.getRange(rangeAddress);
        }

        range.load(["values", "rowCount", "columnCount", "address"]);

        if (includeFormulas) {
          range.load(["formulas"]);
        }

        await context.sync();

        const result: any = {
          address: range.address,
          rowCount: range.rowCount,
          columnCount: range.columnCount,
          values: range.values,
        };

        if (includeFormulas) {
          result.formulas = range.formulas;
        }

        return result;
      });
    },
  });

  // === CELL AND RANGE WRITING ===

  client.registerTool("set_cell_value", {
    description: "Set value in a specific cell",
    schema: {
      type: "object",
      properties: {
        cellAddress: {
          type: "string",
          description: "Cell address (e.g., 'A1')",
        },
        value: {
          type: "string",
          description: "Value to set (string, number, or boolean)",
        },
        sheetName: {
          type: "string",
          description: "Sheet name (optional)",
        },
      },
      required: ["cellAddress", "value"],
    },
    handler: async (args) => {
      const { cellAddress, value, sheetName } = args;

      return Excel.run(async (context) => {
        const worksheet = sheetName
          ? context.workbook.worksheets.getItem(sheetName)
          : context.workbook.worksheets.getActiveWorksheet();

        const cell = worksheet.getRange(cellAddress);
        cell.values = [[value]];

        await context.sync();

        return {
          success: true,
          address: cellAddress,
          value: value,
        };
      });
    },
  });

  client.registerTool("set_range_values", {
    description: "Set values in a range of cells",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range address (e.g., 'A1:C3')",
        },
        values: {
          type: "array",
          description: "2D array of values",
          items: {
            type: "array",
          },
        },
      },
      required: ["rangeAddress", "values"],
    },
    handler: async (args) => {
      const { rangeAddress, values } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);
        range.values = values;

        await context.sync();

        return {
          success: true,
          address: rangeAddress,
          rowsUpdated: values.length,
          columnsUpdated: values[0]?.length || 0,
        };
      });
    },
  });

  // === FORMULA TOOLS ===

  client.registerTool("set_cell_formula", {
    description: "Set formula in a specific cell",
    schema: {
      type: "object",
      properties: {
        cellAddress: {
          type: "string",
          description: "Cell address",
        },
        formula: {
          type: "string",
          description: "Excel formula (e.g., '=SUM(A1:A10)')",
        },
      },
      required: ["cellAddress", "formula"],
    },
    handler: async (args) => {
      const { cellAddress, formula } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const cell = worksheet.getRange(cellAddress);

        // Ensure formula starts with =
        const formulaToSet = formula.startsWith("=") ? formula : `=${formula}`;
        cell.formulas = [[formulaToSet]];

        await context.sync();

        // Get the calculated value
        cell.load(["values"]);
        await context.sync();

        return {
          success: true,
          address: cellAddress,
          formula: formulaToSet,
          calculatedValue: cell.values[0][0],
        };
      });
    },
  });

  // === WORKSHEET MANAGEMENT ===

  client.registerTool("add_worksheet", {
    description: "Add a new worksheet to the workbook",
    schema: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "Name for the new worksheet",
        },
        activate: {
          type: "boolean",
          description: "Activate the new worksheet after creation",
        },
      },
    },
    handler: async (args) => {
      const { name, activate = true } = args;

      return Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        const newSheet = worksheets.add(name);

        if (activate) {
          newSheet.activate();
        }

        newSheet.load(["name", "id"]);
        await context.sync();

        return {
          success: true,
          sheetName: newSheet.name,
          sheetId: newSheet.id,
        };
      });
    },
  });

  client.registerTool("delete_worksheet", {
    description: "Delete a worksheet from the workbook",
    schema: {
      type: "object",
      properties: {
        sheetName: {
          type: "string",
          description: "Name of the worksheet to delete",
        },
      },
      required: ["sheetName"],
    },
    handler: async (args) => {
      const { sheetName } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getItem(sheetName);
        worksheet.delete();
        await context.sync();

        return {
          success: true,
          deletedSheet: sheetName,
        };
      });
    },
  });

  // === TABLE OPERATIONS ===

  client.registerTool("create_table", {
    description: "Create a table from a range",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range address for the table",
        },
        tableName: {
          type: "string",
          description: "Name for the table",
        },
        hasHeaders: {
          type: "boolean",
          description: "Whether the first row contains headers",
        },
      },
      required: ["rangeAddress"],
    },
    handler: async (args) => {
      const { rangeAddress, tableName, hasHeaders = true } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);

        const table = worksheet.tables.add(range, hasHeaders);

        if (tableName) {
          table.name = tableName;
        }

        table.load(["name", "id"]);
        await context.sync();

        return {
          success: true,
          tableName: table.name,
          tableId: table.id,
        };
      });
    },
  });

  client.registerTool("get_tables", {
    description: "Get all tables in the active worksheet",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const tables = worksheet.tables;
        tables.load(["items"]);

        await context.sync();

        const tableInfo = [];
        for (const table of tables.items) {
          table.load(["name", "id"]);
          const range = table.getRange();
          range.load(["address", "rowCount", "columnCount"]);

          await context.sync();

          tableInfo.push({
            name: table.name,
            id: table.id,
            address: range.address,
            rows: range.rowCount,
            columns: range.columnCount,
          });
        }

        return {
          tablesCount: tables.items.length,
          tables: tableInfo,
        };
      });
    },
  });

  // === FORMATTING TOOLS ===

  client.registerTool("format_range", {
    description: "Apply formatting to a range",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range address",
        },
        format: {
          type: "object",
          description: "Formatting options (bold, italic, fontSize, fontColor, fill, etc.)",
        },
      },
      required: ["rangeAddress", "format"],
    },
    handler: async (args) => {
      const { rangeAddress, format } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);
        const rangeFormat = range.format;
        const font = rangeFormat.font;

        if (format.bold !== undefined) font.bold = format.bold;
        if (format.italic !== undefined) font.italic = format.italic;
        if (format.underline) font.underline = format.underline;
        if (format.fontSize) font.size = format.fontSize;
        if (format.fontColor) font.color = format.fontColor;
        if (format.fill) rangeFormat.fill.color = format.fill;
        if (format.horizontalAlignment) rangeFormat.horizontalAlignment = format.horizontalAlignment;
        if (format.verticalAlignment) rangeFormat.verticalAlignment = format.verticalAlignment;
        if (format.numberFormat) range.numberFormat = format.numberFormat;

        await context.sync();

        return {
          success: true,
          rangeFormatted: rangeAddress,
        };
      });
    },
  });

  // === CHART TOOLS ===

  client.registerTool("create_chart", {
    description: "Create a chart from data range",
    schema: {
      type: "object",
      properties: {
        dataRange: {
          type: "string",
          description: "Data range for the chart",
        },
        chartType: {
          type: "string",
          description: "Type of chart",
          enum: ["ColumnClustered", "Line", "Pie", "Bar", "Area", "XYScatter"],
        },
        chartTitle: {
          type: "string",
          description: "Title for the chart",
        },
      },
      required: ["dataRange", "chartType"],
    },
    handler: async (args) => {
      const { dataRange, chartType, chartTitle } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(dataRange);

        const chart = worksheet.charts.add(chartType as any, range, "Auto");

        if (chartTitle) {
          chart.title.text = chartTitle;
        }

        chart.load(["name", "id"]);
        await context.sync();

        return {
          success: true,
          chartName: chart.name,
          chartId: chart.id,
        };
      });
    },
  });

  // === DATA ANALYSIS TOOLS ===

  client.registerTool("analyze_range_statistics", {
    description: "Get statistical analysis of a numeric range",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range address to analyze",
        },
      },
      required: ["rangeAddress"],
    },
    handler: async (args) => {
      const { rangeAddress } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);
        range.load(["values", "rowCount", "columnCount"]);

        await context.sync();

        // Flatten values and filter numbers
        const allValues = range.values.flat().filter(v => typeof v === "number");

        if (allValues.length === 0) {
          return {
            error: "No numeric values found in the range",
          };
        }

        const sum = allValues.reduce((a, b) => a + b, 0);
        const avg = sum / allValues.length;
        const min = Math.min(...allValues);
        const max = Math.max(...allValues);

        // Calculate standard deviation
        const squaredDiffs = allValues.map(v => Math.pow(v - avg, 2));
        const avgSquaredDiff = squaredDiffs.reduce((a, b) => a + b, 0) / allValues.length;
        const stdDev = Math.sqrt(avgSquaredDiff);

        return {
          range: rangeAddress,
          count: allValues.length,
          sum: sum,
          average: avg,
          min: min,
          max: max,
          standardDeviation: stdDev,
        };
      });
    },
  });

  client.registerTool("find_and_replace", {
    description: "Find and replace text in worksheet",
    schema: {
      type: "object",
      properties: {
        findText: {
          type: "string",
          description: "Text to find",
        },
        replaceText: {
          type: "string",
          description: "Text to replace with",
        },
        matchCase: {
          type: "boolean",
          description: "Match case sensitive",
        },
        matchEntireCell: {
          type: "boolean",
          description: "Match entire cell contents",
        },
      },
      required: ["findText", "replaceText"],
    },
    handler: async (args) => {
      const { findText, replaceText, matchCase = false, matchEntireCell = false } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const usedRange = worksheet.getUsedRange();

        if (!usedRange) {
          return { success: false, message: "No data in worksheet" };
        }

        usedRange.load(["values", "rowCount", "columnCount"]);
        await context.sync();

        let replacementCount = 0;
        const newValues = usedRange.values.map(row =>
          row.map(cell => {
            if (typeof cell === "string") {
              if (matchEntireCell) {
                if (matchCase ? cell === findText : cell.toLowerCase() === findText.toLowerCase()) {
                  replacementCount++;
                  return replaceText;
                }
              } else {
                const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), matchCase ? 'g' : 'gi');
                const newValue = cell.replace(regex, replaceText);
                if (newValue !== cell) {
                  replacementCount++;
                  return newValue;
                }
              }
            }
            return cell;
          })
        );

        if (replacementCount > 0) {
          usedRange.values = newValues;
          await context.sync();
        }

        return {
          success: true,
          replacementCount: replacementCount,
        };
      });
    },
  });

  // === SELECTION TOOLS ===

  client.registerTool("get_selected_range", {
    description: "Get the currently selected range",
    schema: {
      type: "object",
      properties: {},
    },
    handler: async () => {
      return Excel.run(async (context) => {
        const selection = context.workbook.getSelectedRange();
        selection.load(["address", "values", "rowCount", "columnCount"]);

        await context.sync();

        return {
          address: selection.address,
          rowCount: selection.rowCount,
          columnCount: selection.columnCount,
          values: selection.values,
        };
      });
    },
  });

  client.registerTool("select_range", {
    description: "Select a specific range",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range to select",
        },
      },
      required: ["rangeAddress"],
    },
    handler: async (args) => {
      const { rangeAddress } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);
        range.select();

        await context.sync();

        return {
          success: true,
          selectedRange: rangeAddress,
        };
      });
    },
  });

  // === ADVANCED OPERATIONS ===

  client.registerTool("sort_range", {
    description: "Sort data in a range",
    schema: {
      type: "object",
      properties: {
        rangeAddress: {
          type: "string",
          description: "Range to sort",
        },
        sortBy: {
          type: "number",
          description: "Column index to sort by (0-based)",
        },
        ascending: {
          type: "boolean",
          description: "Sort in ascending order",
        },
      },
      required: ["rangeAddress", "sortBy"],
    },
    handler: async (args) => {
      const { rangeAddress, sortBy, ascending = true } = args;

      return Excel.run(async (context) => {
        const worksheet = context.workbook.worksheets.getActiveWorksheet();
        const range = worksheet.getRange(rangeAddress);

        range.sort.apply([
          {
            key: sortBy,
            ascending: ascending,
          },
        ]);

        await context.sync();

        return {
          success: true,
          sortedRange: rangeAddress,
        };
      });
    },
  });

  client.registerTool("filter_table", {
    description: "Apply filter to a table",
    schema: {
      type: "object",
      properties: {
        tableName: {
          type: "string",
          description: "Table name",
        },
        columnName: {
          type: "string",
          description: "Column to filter",
        },
        filterValues: {
          type: "array",
          description: "Values to show",
          items: {
            type: "string",
          },
        },
      },
      required: ["tableName", "columnName", "filterValues"],
    },
    handler: async (args) => {
      const { tableName, columnName, filterValues } = args;

      return Excel.run(async (context) => {
        const table = context.workbook.tables.getItem(tableName);
        const column = table.columns.getItem(columnName);

        column.filter.applyValuesFilter(filterValues);
        await context.sync();

        return {
          success: true,
          filteredTable: tableName,
          filteredColumn: columnName,
          filterValues: filterValues,
        };
      });
    },
  });

  console.log("[ExcelClient] ✅ All tools registered successfully");
}

// Export pour réutilisation
export { registerExcelTools };